import useI18n from '@hooks/i18n/useI18n';
import React, { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';
import {
  ICreatePspTokenFromApplePayTokenQueryResult,
  PaymentMethodSource,
  PaymentMethodType,
  ICreatePaymentWith3DSResult,
  IGetPaymentResult,
} from '@monofront/types';
import {
  CREATE_PAYMENT_WITH_3DS,
  CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN,
  GET_PAYMENT,
} from '@monofront/business/queries';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useCheckoutPayment } from '../contexts';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import useApplePayEligibility from '../hooks/useApplePayEligibility';
import useApplePay from '../hooks/useApplePay';
import useLocalizedOrganizationInfos from '@hooks/i18n/useLocalizedOrganizationInfos';
import { useMembershipCouponContext } from '@context/membershipCoupon';
import { getEndOfFreeTrialDate } from '@monofront/business/utils';
import useApplePaySession from '../hooks/useApplePaySession';

interface IPayWithApplePayCtaProps {
  type?: 'plain' | 'check-out';
  onPaymentAuthorized: (
    id: string,
    paymentMethodType: PaymentMethodType,
    paymentMethodSource?: PaymentMethodSource,
    financialPaymentId?: string,
  ) => void;
  height?: number;
  radius?: number;
  isMembershipCardUpdate?: boolean;
}

const SubscribeWithApplePay = ({
  type = 'check-out',
  onPaymentAuthorized,
  height,
  radius,
  isMembershipCardUpdate = false,
}: IPayWithApplePayCtaProps) => {
  const { url } = useLocalizedOrganizationInfos();
  const { discountedPrice } = useMembershipCouponContext();
  const { locale } = useI18n();
  const tAccount = useTranslations('Account');
  const tCart = useTranslations('Cart');
  const { handlePaymentError } = useHandlePaymentError();
  const buttonRef = useRef<HTMLElement>(null);
  const { setIsOrderValidationInProgress, selectedNewApplePay } =
    useCheckoutPayment();
  const { requestPayload } = useApplePay();
  const { isApplePayAvailable, isCheckingApplePayAvailability } =
    useApplePayEligibility();
  const [createPspTokenFromApplePayToken] =
    useMutation<ICreatePspTokenFromApplePayTokenQueryResult>(
      CREATE_PSP_TOKEN_FROM_APPLE_PAY_TOKEN,
    );
  const [createPaymentWith3DS] = useMutation<ICreatePaymentWith3DSResult>(
    CREATE_PAYMENT_WITH_3DS,
  );
  const [getPayment] = useLazyQuery<IGetPaymentResult>(GET_PAYMENT);
  const paymentRequestPayload = requestPayload(0);
  const enhancedPaymentRequestPayload: ApplePayJS.ApplePayPaymentRequest = {
    ...paymentRequestPayload,
    recurringPaymentRequest: {
      managementURL: `${url}/pages/manage-my-subscription`,
      paymentDescription: 'Recurring payment',
      regularBilling: {
        label: tAccount('subscriptionRecurring'),
        amount: discountedPrice.toString(),
        paymentTiming: 'recurring',
        recurringPaymentIntervalUnit: 'year',
        recurringPaymentStartDate: new Date(getEndOfFreeTrialDate()),
      },
      ...(!isMembershipCardUpdate && {
        trialBilling: {
          label: tCart('MembershipProductCart.productName'),
          amount: '0.00',
          paymentTiming: 'recurring',
        },
      }),
      billingAgreement: isMembershipCardUpdate
        ? tAccount('applePayUpdateMembershipCard')
        : tAccount('subscriptionBillingAgreement'),
    },
  };

  const handleSubscription = async (
    applePayToken: ApplePayJS.ApplePayPaymentToken,
  ) => {
    try {
      const { data: createPspTokenFromApplePayTokenData } =
        await createPspTokenFromApplePayToken({
          variables: {
            token: applePayToken.paymentData,
          },
        });

      if (
        !createPspTokenFromApplePayTokenData?.createPspTokenFromApplePayToken
          ?.token
      ) {
        throw new Error('No token generated for the user new apple pay card.');
      }

      const asyncPayment = await createPaymentWith3DS({
        variables: {
          token:
            createPspTokenFromApplePayTokenData?.createPspTokenFromApplePayToken
              ?.token,
          paymentMethodSource: PaymentMethodSource.CHECKOUT,
        },
      });

      if (!asyncPayment.data) {
        throw new Error('Failed to generate payment for Apple Pay');
      }

      const { data: paymentData } = await getPayment({
        variables: {
          paymentId: asyncPayment?.data?.createPaymentWith3DS.id,
        },
      });

      if (!paymentData?.getPayment?.paymentMethodId) {
        throw new Error('Failed to get payment for Apple Pay');
      }

      onPaymentAuthorized(
        paymentData.getPayment.paymentMethodId,
        PaymentMethodType.CB,
        PaymentMethodSource.CHECKOUT,
        asyncPayment?.data?.createPaymentWith3DS.id,
      );

      return true;
    } catch (error: any) {
      setIsOrderValidationInProgress(false);

      handlePaymentError({
        code: 'PAY_WITH_NEW_CARD_FAILED',
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot create a PaymentIntent for a new card or complete an order for a non-3DS card.',
        shouldDisplayError: true,
        error,
        context: {
          selected_apple_pay_status: selectedNewApplePay,
        },
      });

      return false;
    }
  };

  const { createSession } = useApplePaySession({
    onPaymentAuthorized: handleSubscription,
    paymentRequestPayload: enhancedPaymentRequestPayload,
  });

  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.addEventListener('click', createSession);

      return () =>
        buttonRef.current?.removeEventListener('click', createSession);
    }
  }, [createSession]);

  if (isCheckingApplePayAvailability) {
    return <HeartLoader />;
  }

  if (!isApplePayAvailable) {
    return null;
  }

  return (
    <>
      <apple-pay-button
        ref={buttonRef}
        buttonstyle="black"
        type={type}
        locale={locale}
      />
      <style jsx global>{`
        apple-pay-button {
          --apple-pay-button-border-radius: ${radius || '100'}px;
          --apple-pay-button-height: ${height || '44'}px;
          --apple-pay-button-width: 100%;
        }
        ${height ? `apple-pay-button > span { height: 70%; }` : ''}
      `}</style>
    </>
  );
};

export default SubscribeWithApplePay;
